import React, { useCallback, useState } from 'react';
import { Search, X } from 'lucide-react';
import Button from '../../ui/Button';
import Checkbox from '../../ui/Checkbox';
import IconButton from '../../ui/IconButton';
import Popover from '../../ui/Popover';
import TextInput from '../../ui/TextInput';
import { cx } from '../../ui/classNames';
import { getColumnLabel } from './gridColumns';

// MUI's FormControlLabel: the checkbox's target reaches 11px into the
// padding, then body1 text (smaller in compact mode).
const LABEL_CLASS_NAME = '-ml-[11px] mr-4 inline-flex cursor-pointer items-center text-[14px] leading-[1.5] '
    + '[[data-compact]_&]:text-[12px] has-[:disabled]:cursor-default has-[:disabled]:text-ui-disabled-text';
// The search field hides the browser's own search decorations and clear
// button; the panel has its own.
const SEARCH_CLASS_NAME = cx(
    '!px-[11px] [&_input::-webkit-search-cancel-button]:hidden [&_input::-webkit-search-decoration]:hidden',
    '[&_input::-webkit-search-results-button]:hidden [&_input::-webkit-search-results-decoration]:hidden',
);
// Reset keeps a minimum width.
const RESET_CLASS_NAME = '!min-w-16';

function isHideable(column) {
    return column.getCanHide();
}

function isVisible(visibility, columnId) {
    return visibility?.[columnId] !== false;
}

function sameVisibility(left = {}, right = {}) {
    const leftHidden = Object.keys(left).filter((key) => left[key] === false);
    const rightHidden = Object.keys(right).filter((key) => right[key] === false);
    return leftHidden.length === rightHidden.length
        && leftHidden.every((key) => right[key] === false);
}

// "Manage columns": the column-visibility panel, below the grid's header
// (`anchorEl`). Lists every column (TanStack columns, in definition order)
// with a search box, a Show/Hide All toggle and a Reset back to the
// visibility the panel opened with. It holds focus while open; Escape
// closes it with `onClose({ restoreFocus: true })`, which focuses the
// header, and a press outside closes it where the pointer went. The grid
// mounts it only while open, so the search starts empty each time.
export default function WatchlistGridColumnsPanel({
    anchorEl,
    columns,
    columnVisibility,
    initialColumnVisibility,
    onColumnVisibilityChange,
    onClose,
}) {
    const [searchValue, setSearchValue] = useState('');
    // Focus once on mount without scrolling the page to the not-yet-placed panel.
    const focusSearch = useCallback((node) => {
        node?.focus({ preventScroll: true });
    }, []);
    const query = searchValue.trim().toLowerCase();
    const shownColumns = query
        ? columns.filter((column) => getColumnLabel(column).toLowerCase().includes(query))
        : columns;
    const hideableColumns = shownColumns.filter(isHideable);
    const allVisible = hideableColumns.every((column) => isVisible(columnVisibility, column.id));
    const allHidden = hideableColumns.every((column) => !isVisible(columnVisibility, column.id));

    const toggleColumn = (columnId) => {
        onColumnVisibilityChange({
            ...columnVisibility,
            [columnId]: !isVisible(columnVisibility, columnId),
        });
    };
    const toggleAll = () => {
        const next = { ...columnVisibility };
        columns.filter(isHideable).forEach((column) => {
            if (allVisible) {
                next[column.id] = false;
            } else {
                delete next[column.id];
            }
        });
        onColumnVisibilityChange(next);
    };

    return (
        <Popover
            anchor={anchorEl}
            open
            onOpenChange={(next, _event, reason) => {
                if (!next) onClose({ restoreFocus: reason === 'escape-key' });
            }}
            offset={0}
            modal
            returnFocus={false}
            aria-label="Manage columns"
            className="flex max-h-[450px] min-w-[300px] max-w-[calc(100vw-4px)] flex-col overflow-auto !shadow-none"
        >
            <div className="px-6 py-3">
                <TextInput
                    ref={focusSearch}
                    type="search"
                    placeholder="Search"
                    autoComplete="off"
                    aria-label="Search"
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    className={SEARCH_CLASS_NAME}
                    startAdornment={(
                        <span className="mr-[15px] inline-flex text-black/[0.54] dark:text-white">
                            <Search aria-hidden="true" className="size-6 [[data-compact]_&]:size-[20.5px]" />
                        </span>
                    )}
                    endAdornment={(
                        <IconButton
                            label="Clear"
                            tabIndex={-1}
                            className={searchValue ? undefined : 'invisible'}
                            onClick={() => setSearchValue('')}
                        >
                            <X aria-hidden="true" className="size-5 [[data-compact]_&]:size-[17px]" />
                        </IconButton>
                    )}
                />
            </div>
            <div className="flex max-h-[400px] flex-[1_1_0%] flex-col items-start overflow-auto px-6 pb-3">
                {shownColumns.map((column) => (
                    <label key={column.id} className={LABEL_CLASS_NAME}>
                        <Checkbox
                            name={column.id}
                            disabled={!isHideable(column)}
                            checked={isVisible(columnVisibility, column.id)}
                            onChange={() => toggleColumn(column.id)}
                        />
                        {getColumnLabel(column) || column.id}
                    </label>
                ))}
                {shownColumns.length === 0 && (
                    <div className="py-1 text-[#9e9e9e]">No columns</div>
                )}
            </div>
            {shownColumns.length > 0 && (
                <div className="flex justify-between border-0 border-t border-solid border-hairline py-1 pl-6 pr-2">
                    <label className={LABEL_CLASS_NAME}>
                        <Checkbox
                            name="Show/Hide All"
                            disabled={hideableColumns.length === 0}
                            checked={allVisible}
                            indeterminate={!allVisible && !allHidden}
                            onChange={toggleAll}
                        />
                        Show/Hide All
                    </label>
                    <Button
                        className={RESET_CLASS_NAME}
                        disabled={sameVisibility(columnVisibility, initialColumnVisibility)}
                        onClick={() => onColumnVisibilityChange(initialColumnVisibility || {})}
                    >
                        Reset
                    </Button>
                </div>
            )}
        </Popover>
    );
}
